import type { HTMLAttributes, ReactNode } from "react";
import { CaretRight } from "@phosphor-icons/react";
import { cn } from "./cn";

/** A card of rows split by hairlines. Holds `ListRow`s. */
export function List({ className, children, ...rest }: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={cn("divide-y divide-line overflow-hidden rounded-card bg-surface shadow-card", className)} {...rest}>
      {children}
    </ul>
  );
}

export type ListRowProps = {
  /** A company logo or icon tile, about 40px. */
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  /** Non-interactive content on the right, usually a `StatusBadge`. */
  trailing?: ReactNode;
  /** Buttons on the right. They sit above the row's own click target. */
  actions?: ReactNode;
  onClick?: () => void;
  /** Defaults to on when the row opens something. */
  chevron?: boolean;
  disabled?: boolean;
  className?: string;
};

/**
 * One row: logo tile, title over a muted meta line, a badge on the right and a chevron.
 * The whole row is the click target when it has `onClick`.
 */
export function ListRow({ leading, title, meta, trailing, actions, onClick, chevron, disabled, className }: ListRowProps) {
  const opens = Boolean(onClick) && !disabled;
  const target = "min-w-0 flex-1 text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-primary";
  const text = (
    <>
      <span className="block truncate text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</span>
      {meta ? <span className="mt-0.5 block truncate text-[13px] text-muted">{meta}</span> : null}
    </>
  );
  return (
    <li className={cn("relative flex items-center gap-3.5 px-4 py-3.5 transition-[background-color] duration-150", opens && "hover:bg-sunk/70", className)}>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      {onClick ? (
        <button type="button" className={target} onClick={onClick} disabled={disabled}>
          {text}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{text}</div>
      )}
      {trailing ? <span className="pointer-events-none flex shrink-0 items-center gap-2">{trailing}</span> : null}
      {actions ? <span className="relative z-[1] flex shrink-0 items-center gap-1">{actions}</span> : null}
      {chevron ?? opens ? <CaretRight size={16} weight="bold" className="pointer-events-none shrink-0 text-muted" aria-hidden /> : null}
    </li>
  );
}
